import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ConnexManageDriversDrawerComponent } from './connex-manage-drivers-drawer.component';
import {
	AdminConnexDriverAssignmentSlot,
	AdminXpressConnexService,
	AppDataService,
	ConfigService,
	DriverAssignmentRouteStatusFilterEnum,
	DriverAssignmentStatusFilterEnum,
	emptyBaseAdminRequest,
	GetAdminConnexDriverAssignmentsRequest
} from 'app/services';
import { MenuPermissionActionEnum } from 'app/models';
import { SlotFilterEnum } from 'app/models/enums/slot-filter.enum';
import { Utils } from 'app/utils';
import { MessageService } from 'primeng/api';
import { SelectItem } from 'primeng/api';

interface AssigningDriversKpiCard {
	title: string;
	value: string;
	icon: string;
}

interface ConnexRouteFilterOption {
	label: string;
	value: string;
	routeStatusFilter: DriverAssignmentRouteStatusFilterEnum | null;
	routeId: number | null;
}

interface SlotStatusLabel {
	text: string;
	tone: 'danger' | 'warning' | 'success';
}

@Component({
	selector: 'connex-assigning-drivers-tab',
	templateUrl: './connex-assigning-drivers-tab.component.html',
	styleUrls: ['./connex-assigning-drivers-tab.component.scss']
})
export class ConnexAssigningDriversTabComponent implements OnInit, OnDestroy {
	private static readonly pesoFmt = new Intl.NumberFormat('en-PH', {
		maximumFractionDigits: 0,
		minimumFractionDigits: 0
	});

	@Input()
	public menuPermissionActive!: string;

	@ViewChild('manageDriversDrawer')
	private manageDriversDrawer?: ConnexManageDriversDrawerComponent;

	public loading = false;
	public hasLoaded = false;
	public slots: AdminConnexDriverAssignmentSlot[] = [];

	public selectedDate = Utils.getPHTodayCalendarDate();

	public routeFilterKey = ConnexAssigningDriversTabComponent.defaultRouteFilter.value;
	public statusFilter: DriverAssignmentStatusFilterEnum = DriverAssignmentStatusFilterEnum.All;
	public slotFilter: SlotFilterEnum = SlotFilterEnum.All;

	private static readonly defaultRouteFilter: ConnexRouteFilterOption = {
		label: 'All Active Routes',
		value: 'active',
		routeStatusFilter: DriverAssignmentRouteStatusFilterEnum.ActiveRoute,
		routeId: null
	};

	private static readonly baseRouteOptions: ConnexRouteFilterOption[] = [
		ConnexAssigningDriversTabComponent.defaultRouteFilter,
		{
			label: 'Inactive Routes',
			value: 'inactive',
			routeStatusFilter: DriverAssignmentRouteStatusFilterEnum.InactiveRoute,
			routeId: null
		}
	];

	public readonly slotOptions: SelectItem<SlotFilterEnum>[] = [
		{ label: 'All Slots', value: SlotFilterEnum.All },
		{ label: 'Active Slots', value: SlotFilterEnum.Active },
		{ label: 'Inactive Slots', value: SlotFilterEnum.Inactive }
	];

	public routeOptions: ConnexRouteFilterOption[] = [...ConnexAssigningDriversTabComponent.baseRouteOptions];

	public readonly statusOptions: SelectItem<DriverAssignmentStatusFilterEnum>[] = [
		{ label: 'All Statuses', value: DriverAssignmentStatusFilterEnum.All },
		{ label: 'No Drivers Assigned', value: DriverAssignmentStatusFilterEnum.NoDriversAssigned },
		{ label: 'Partially Assigned', value: DriverAssignmentStatusFilterEnum.PartiallyStaffed },
		{ label: 'Fully Booked', value: DriverAssignmentStatusFilterEnum.FullyBooked },
		{ label: 'Ready', value: DriverAssignmentStatusFilterEnum.Ready },
		{ label: 'Drivers Staffed', value: DriverAssignmentStatusFilterEnum.DriversStaffed },
		{ label: 'Slot Disabled', value: DriverAssignmentStatusFilterEnum.SlotDisabled }
	];

	public kpiCards: AssigningDriversKpiCard[] = [];

	private filterDebounceTimer: number | undefined;

	constructor(
		public configService: ConfigService,
		private appData: AppDataService,
		private adminConnexService: AdminXpressConnexService,
		private messageService: MessageService
	) {}

	ngOnInit(): void {
		void this.loadAssignments();
	}

	ngOnDestroy(): void {
		if (this.filterDebounceTimer) {
			window.clearTimeout(this.filterDebounceTimer);
		}
	}

	public onFiltersChanged(): void {
		if (this.filterDebounceTimer) {
			window.clearTimeout(this.filterDebounceTimer);
		}
		this.filterDebounceTimer = window.setTimeout(() => {
			this.filterDebounceTimer = undefined;
			void this.loadAssignments();
		}, 300);
	}

	public shiftDate(days: number): void {
		this.selectedDate = Utils.addCalendarDays(this.selectedDate, days);
		void this.loadAssignments();
	}

	public onDatePicked(): void {
		this.selectedDate = Utils.normalizeDateOnly(this.selectedDate);
		void this.loadAssignments();
	}

	public onManageDrivers(slot: AdminConnexDriverAssignmentSlot): void {
		if (!this.canManageDrivers) {
			return;
		}
		this.manageDriversDrawer?.open(slot, this.selectedDate);
	}

	public get canManageDrivers(): boolean {
		return !!this.menuPermissionActive
			&& this.appData.hasPermission(this.menuPermissionActive, MenuPermissionActionEnum.Update);
	}

	public slotTimeParts(departureAt: string): { dateLine: string; timeLine: string; meridiem: string } {
		const parts = ConnexAssigningDriversTabComponent.parseOffsetDateTimeWithoutTimezone(departureAt);
		if (!parts) {
			return { dateLine: '—', timeLine: '—', meridiem: '' };
		}
		const dateLine = new Date(parts.year, parts.month - 1, parts.day)
			.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
		const timeFmt = new Date(2000, 0, 1, parts.hour, parts.minute)
			.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
		const timeParts = timeFmt.split(' ');
		const meridiem = timeParts.length > 1 ? timeParts[timeParts.length - 1] : '';
		const timeLine = timeParts[0] ?? timeFmt;
		return { dateLine, timeLine, meridiem };
	}

	public slotMetaLine(slot: AdminConnexDriverAssignmentSlot): string {
		const fare = ConnexAssigningDriversTabComponent.pesoFmt.format(slot.FareAmount);
		const toll = slot.TollAmount > 0 ? 'Toll included' : 'No toll';
		const window = this.formatBookingWindow(slot.BookingWindowMinutes);
		const drivers =
			slot.AssignedDriverCount > 0
				? `${slot.AssignedDriverCount} driver${slot.AssignedDriverCount === 1 ? '' : 's'} assigned`
				: `${slot.RequiredDriverCount} driver${slot.RequiredDriverCount === 1 ? '' : 's'} required`;
		return `₱${fare} fare · ${toll} · ${window} · ${drivers}`;
	}

	public assignedDriverChipLabel(driver: { Name: string; PlateNumber: string }): string {
		const plate = (driver.PlateNumber ?? '').trim();
		return plate ? `${driver.Name} · ${plate}` : driver.Name;
	}

	public driversCountClass(slot: AdminConnexDriverAssignmentSlot): string {
		return slot.AssignedDriverCount <= 0
			? 'cad-slot-card__metric-value--alert'
			: 'cad-slot-card__metric-value--assigned';
	}

	public slotStatusLabels(slot: AdminConnexDriverAssignmentSlot): SlotStatusLabel[] {
		return [
			this.driverAssignmentLabel(slot),
			this.bookingAvailabilityLabel(slot)
		].filter((label): label is SlotStatusLabel => !!label);
	}

	private driverAssignmentLabel(slot: AdminConnexDriverAssignmentSlot): SlotStatusLabel | null {
		const totalVehicles = Math.max(0, slot.RequiredDriverCount ?? 0);
		const assignedDrivers = Math.max(0, slot.AssignedDriverCount ?? 0);

		if (assignedDrivers <= 0) {
			return { text: 'No Drivers Assigned', tone: 'danger' };
		}
		if (assignedDrivers < totalVehicles) {
			return { text: 'Partially Assigned', tone: 'warning' };
		}
		if (totalVehicles > 0 && assignedDrivers === totalVehicles) {
			return { text: 'Ready', tone: 'success' };
		}
		if (totalVehicles > 0 && assignedDrivers > totalVehicles) {
			return { text: 'Over Assigned', tone: 'danger' };
		}

		return null;
	}

	private bookingAvailabilityLabel(slot: AdminConnexDriverAssignmentSlot): SlotStatusLabel | null {
		const booked = Math.max(0, slot.BookedCount ?? 0);
		const capacity = Math.max(0, slot.CapacityCount ?? 0);

		if (capacity <= 0 || booked <= 0) {
			return null;
		}
		if (booked >= capacity) {
			return { text: 'Fully Booked', tone: 'success' };
		}
		return { text: 'Partially Booked', tone: 'warning' };
	}

	public formatHeadingDate(d: Date): string {
		return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
	}

	private get selectedRouteFilter(): ConnexRouteFilterOption {
		return (
			this.routeOptions.find((opt) => opt.value === this.routeFilterKey) ??
			ConnexAssigningDriversTabComponent.defaultRouteFilter
		);
	}

	public async loadAssignments(): Promise<void> {
		this.loading = true;
		const routeFilter = this.selectedRouteFilter;
		const body: GetAdminConnexDriverAssignmentsRequest = {
			...emptyBaseAdminRequest(),
			ServiceDate: Utils.formatPHCalendarDate(this.selectedDate),
			RouteId: routeFilter.routeId,
			RouteStatusFilter: routeFilter.routeId == null ? routeFilter.routeStatusFilter : null,
			StatusFilter: this.statusFilter,
			SlotFilter: this.slotFilter
		};

		try {
			const res = await this.adminConnexService.getConnexDriverAssignments(body, this.messageService);
			this.hasLoaded = true;
			if (!res || res.Success === false) {
				this.slots = [];
				this.applyKpis({
					TotalSlots: 0,
					Ready: 0,
					NoDrivers: 0,
					DriversStaffed: 0,
					BookedCount: 0,
					CapacityCount: 0
				});
				return;
			}

			this.slots = (res.Items ?? []).map((slot) => ({
				...slot,
				AssignedDrivers: slot.AssignedDrivers ?? []
			}));
			this.applyKpis(res.Stats);
			const routeItems = (res.RouteOptions ?? []).map((r) => ({
				label: r.RouteName,
				value: `route-${r.RouteId}`,
				routeStatusFilter: null,
				routeId: r.RouteId
			}));
			this.routeOptions = [
				...ConnexAssigningDriversTabComponent.baseRouteOptions,
				...routeItems
			];
			if (!this.routeOptions.some((opt) => opt.value === this.routeFilterKey)) {
				this.routeFilterKey = ConnexAssigningDriversTabComponent.defaultRouteFilter.value;
			}
		} finally {
			this.loading = false;
		}
	}

	private applyKpis(stats: {
		TotalSlots: number;
		Ready: number;
		NoDrivers: number;
		DriversStaffed: number;
		BookedCount: number;
		CapacityCount: number;
	}): void {
		const bookedLabel =
			stats.CapacityCount > 0 ? `${stats.BookedCount}/${stats.CapacityCount}` : `${stats.BookedCount}`;

		this.kpiCards = [
			{ title: 'Total Slot', value: `${stats.TotalSlots}`, icon: 'pi pi-th-large' },
			{ title: 'Ready', value: `${stats.Ready}`, icon: 'pi pi-check-circle' },
			{ title: 'No Drivers', value: `${stats.NoDrivers}`, icon: 'pi pi-exclamation-circle' },
			{ title: 'Drivers Staffed', value: `${stats.DriversStaffed}`, icon: 'pi pi-users' },
			{ title: 'Booked', value: bookedLabel, icon: 'pi pi-car' }
		];
	}

	private formatBookingWindow(minutes: number): string {
		if (minutes >= 60 && minutes % 60 === 0) {
			const hrs = minutes / 60;
			return `${hrs}h window`;
		}
		if (minutes >= 60) {
			const rounded = Math.round((minutes / 60) * 10) / 10;
			return `${rounded}h window`;
		}
		return `${minutes} min window`;
	}

	private static parseOffsetDateTimeWithoutTimezone(value: string): {
		year: number;
		month: number;
		day: number;
		hour: number;
		minute: number;
	} | null {
		const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value ?? '');
		if (!match) {
			return null;
		}

		const [, year, month, day, hour, minute] = match;
		return {
			year: Number(year),
			month: Number(month),
			day: Number(day),
			hour: Number(hour),
			minute: Number(minute)
		};
	}
}
